import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, Receipt, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = [
  "Kira",
  "Personel Maaşı",
  "Yazılım/Abonelik",
  "Elektrik/Su/Doğalgaz",
  "İnternet/Telefon",
  "Muhasebe",
  "Vergi/SGK",
  "Sigorta",
  "Diğer Sabit Gider",
];

const FixedExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    amount: "",
    day_of_month: 1,
    active: true,
    notes: "",
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/fixed-expenses`, { withCredentials: true });
      setExpenses(data);
    } catch (error) {
      toast.error("Sabit giderler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        day_of_month: parseInt(formData.day_of_month, 10),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/fixed-expenses/${editing.id}`, payload, { withCredentials: true });
        toast.success("Sabit gider güncellendi");
      } else {
        await axios.post(`${API_URL}/api/fixed-expenses`, payload, { withCredentials: true });
        toast.success("Sabit gider eklendi");
      }
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sabit gideri silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/fixed-expenses/${id}`, { withCredentials: true });
      toast.success("Silindi");
      fetchData();
    } catch (error) {
      toast.error("Silinirken hata oluştu");
    }
  };

  const handleToggle = async (exp) => {
    try {
      await axios.put(`${API_URL}/api/fixed-expenses/${exp.id}`, { active: !exp.active }, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const handleEdit = (exp) => {
    setEditing(exp);
    setFormData({
      name: exp.name,
      category: exp.category,
      amount: exp.amount.toString(),
      day_of_month: exp.day_of_month,
      active: exp.active,
      notes: exp.notes || "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({ name: "", category: "", amount: "", day_of_month: 1, active: true, notes: "" });
  };

  const totalMonthly = expenses.filter((e) => e.active).reduce((s, e) => s + e.amount, 0);
  const totalYearly = totalMonthly * 12;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div></div>;
  }

  return (
    <div className="p-8" data-testid="fixed-expenses-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">Sabit Giderler</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">Her ay tekrarlanan giderlerinizi yönetin (kira, maaş, abonelikler vb.)</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-fixed-expense-button"
          className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-sm shadow-premium hover:opacity-90 premium-hover"
        >
          <Plus size={18} weight="bold" />
          Yeni Sabit Gider
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))]">Aktif Sabit Gider</p>
            <Receipt size={20} weight="duotone" className="text-[hsl(var(--text-soft))]" />
          </div>
          <p className="text-3xl font-display font-bold text-[hsl(var(--text-strong))] tabular-nums">{expenses.filter(e => e.active).length}</p>
          <p className="text-xs text-[hsl(var(--text-faint))] mt-1">Toplam {expenses.length} kayıt</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Aylık Toplam</p>
          <p className="text-3xl font-display font-bold text-[hsl(var(--text-strong))] tabular-nums">₺{totalMonthly.toLocaleString("tr-TR")}</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-premium">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70 mb-2">Yıllık Projeksiyon</p>
          <p className="text-3xl font-display font-bold tabular-nums">₺{totalYearly.toLocaleString("tr-TR")}</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Ad</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Kategori</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Ödeme Günü</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tutar</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Durum</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-16 text-center">
                  <div className="text-sm text-[hsl(var(--text-faint))]">Henüz sabit gider eklenmemiş</div>
                  <button onClick={() => setShowModal(true)} className="mt-3 text-sm text-[hsl(var(--text-strong))] underline font-medium">İlk sabit giderinizi ekleyin</button>
                </td></tr>
              ) : expenses.map((exp, idx) => (
                <tr key={exp.id} className={`border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40 ${!exp.active ? 'opacity-50' : ''}`} data-testid={`fixed-expense-row-${idx}`}>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-[hsl(var(--text-strong))] font-medium">{exp.name}</p>
                    {exp.notes && <p className="text-xs text-[hsl(var(--text-faint))] mt-0.5">{exp.notes}</p>}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">{exp.category}</td>
                  <td className="px-5 py-3.5 text-sm text-center text-[hsl(var(--text-soft))] tabular-nums">Ayın {exp.day_of_month}.</td>
                  <td className="px-5 py-3.5 text-sm text-right font-semibold text-[hsl(var(--text-strong))] tabular-nums">₺{exp.amount.toLocaleString("tr-TR")}</td>
                  <td className="px-5 py-3.5 text-center">
                    <button onClick={() => handleToggle(exp)} data-testid={`toggle-fixed-expense-${idx}`}>
                      {exp.active ? (
                        <ToggleRight size={28} weight="fill" className="text-[hsl(var(--success))]" />
                      ) : (
                        <ToggleLeft size={28} weight="fill" className="text-[hsl(var(--text-faint))]" />
                      )}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleEdit(exp)} data-testid={`edit-fixed-expense-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]">
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(exp.id)} data-testid={`delete-fixed-expense-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--danger))]">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-md" data-testid="fixed-expense-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">
              {editing ? "Sabit Gideri Düzenle" : "Yeni Sabit Gider"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="fixed-expense-form">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Gider Adı *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Örn: Ofis kirası" data-testid="fixed-expense-name-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Kategori *</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required data-testid="fixed-expense-category-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Seçiniz</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Aylık Tutar *</label>
                <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required data-testid="fixed-expense-amount-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Ödeme Günü *</label>
                <input type="number" min={1} max={31} value={formData.day_of_month} onChange={(e) => setFormData({ ...formData, day_of_month: e.target.value })} required data-testid="fixed-expense-day-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Notlar</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="fixed-expense-notes-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} data-testid="fixed-expense-active-checkbox" className="w-4 h-4 rounded border-[hsl(var(--border))]" />
              <span className="text-sm text-[hsl(var(--text-strong))]">Aktif (aylık toplama dahil edilsin)</span>
            </label>

            <div className="flex gap-2 pt-3">
              <button type="button" onClick={handleClose} data-testid="cancel-fixed-expense-button" className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">İptal</button>
              <button type="submit" data-testid="save-fixed-expense-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Kaydet</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FixedExpensesPage;
